<template>
  <div
    :id="id"
    ref="item"
    role="listitem"
    variant="tertiary"
    class="SearchECCarouselItem">
    <ai-button
      variant="tertiary"
      :aria-label="buttonAriaLabel"
      @click="selectExperienceCategory(id)">
      <ai-icon
        v-if="iconName"
        :class="{ 'SearchECCarouselItem-Icon': isSelected }"
        :name="iconName"
        :size="{ height: 40, width: 40 }" />
      <img v-else :src="src" :alt="alt ?? ''" width="40" height="40" />

      <ai-typo as="span" variant="icon-text" class="SearchECCarouselItem-Label">
        {{ label }}
      </ai-typo>

      <div
        :class="{
          'SearchECCarouselItem-Selected': isSelected,
          'SearchECCarouselItem-NotSelected': !isSelected,
        }" />
    </ai-button>
  </div>
</template>

<script setup lang="ts">
import type {
  AmenitiesName,
  IconName,
  LogoName,
  PictogramsName,
} from '~~/domains/ux/atoms/AiIcon/types';
import { useSearchStore } from '~~/domains/search';

const { t } = useI18n();

const searchStore = useSearchStore();

type Props = {
  label: string;
  iconName?: IconName | PictogramsName | AmenitiesName | LogoName;
  src?: string;
  alt?: string;
  id: string;
};

const props = withDefaults(defineProps<Props>(), {
  alt: '',
  iconName: undefined,
  src: '',
});

const isSelected = computed(
  () => searchStore.experienceCategoryId === props.id,
);

const selectExperienceCategory = (id: string | null) => {
  searchStore.loading = true;
  setTimeout(() => {
    searchStore.experienceCategoryId = id;
    searchStore.loading = false;
  }, 200);
};

const item = ref<HTMLElement>();

const buttonAriaLabel = computed(() => {
  if (!isSelected.value) {
    return undefined;
  }

  return (
    t('accessibility.description.experienceCategorySelected') +
    ' ' +
    props.label
  );
});

defineExpose({
  item,
});
</script>

<style scoped lang="scss">
@use '@/assets/styles/utilities/colors';
@use '@/assets/styles/utilities/constants';
@use '@/assets/styles/utilities/mq';
@use '@/assets/styles/utilities/mixins';

.SearchECCarouselItem button {
  display: flex;
  flex-direction: column;
  align-items: center;
  height: auto;
  cursor: pointer;

  @include mixins.rem-fallback(min-width, 64);

  @media (mq.$from-medium) {
    @include mixins.rem-fallback(min-width, 96);
  }
}

.SearchECCarouselItem-Icon {
  fill: colors.$gold-800;
}

.SearchECCarouselItem-Label {
  margin-top: constants.$margin-00;
  text-transform: uppercase;
  color: colors.$blue-600;
  text-align: center;
  white-space: nowrap;
}

.SearchECCarouselItem-Selected {
  margin-top: constants.$margin-00;
  border-bottom: 4px solid colors.$gold-800;
  @include mixins.rem-fallback(width, 40);
}

.SearchECCarouselItem-NotSelected {
  // Add the size of the border-bottom of the selected one to avoid icons to move
  height: calc(constants.$margin-00 + 4px);
  @include mixins.rem-fallback(width, 40);

}
</style>
